import { useState } from "react";

import { faCheck, faEye } from "@fortawesome/pro-regular-svg-icons";
import { noop } from "lodash-es";

import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import {
  FloatingFormTitle,
  FloatingFormWindow,
} from "src/cases/caseView/components/FloatingFormWindow";
import { HistorySingleEntry } from "src/cases/caseView/history/HistoryItemFrame";
import {
  useCaseTaskFormSchema,
  useGetCaseTemplateTask,
} from "src/cases/caseView/hooks";
import { useCase } from "src/cases/queries/cases";
import { CaseHistoryEvent } from "src/cases/queries/casesTypes";
import { Tooltip } from "src/design-system/Tooltip";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const TaskCompleted: React.FC<{
  entry: CaseHistoryEvent<"task_completed">;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);
  const getCaseTemplateTask = useGetCaseTemplateTask(caseData);

  const { task_key, task_form_data } = entry.event_data;
  const templateTask = getCaseTemplateTask(task_key);
  const taskDisplayName = templateTask?.display_name || task_key;

  const [isFormOpen, setIsFormOpen] = useState(false);
  const taskFormSchema = useCaseTaskFormSchema(templateTask);

  const hasFormFields =
    templateTask?.form?.fields && templateTask.form.fields.length > 0;
  const hasFormData = task_form_data && Object.keys(task_form_data).length > 0;
  const shouldShowForm = hasFormFields && hasFormData;

  return (
    <HistorySingleEntry
      actorId={entry.user_id}
      eventType={entry.event_type}
      headline={
        <>
          <Pill size="sm" variant="gray">
            <Pill.Text>{taskDisplayName}</Pill.Text>
          </Pill>{" "}
          task was marked as complete.
        </>
      }
      icon={faCheck}
      isLast={isLast}
      sideAction={
        shouldShowForm ? (
          <FloatingFormWindow
            button={
              <Tooltip
                delay={250}
                placement="top-end"
                title="View form"
                asChild
              >
                <Icon
                  color="text-gray-500 hover:text-gray-700"
                  dataLoc={`task-view-form-${task_key}`}
                  icon={faEye}
                  size="sm"
                  onClick={() => setIsFormOpen(true)}
                />
              </Tooltip>
            }
            defaultValues={task_form_data}
            disabled={true}
            fields={templateTask.form?.fields ?? []}
            isOpen={isFormOpen}
            schema={taskFormSchema}
            title={
              <FloatingFormTitle
                description={templateTask.description ?? undefined}
                task={{
                  // We do not need task id in history, it is just to display the form
                  id: "",
                  key: task_key,
                  title: taskDisplayName,
                  isCompleted: true,
                }}
                title={taskDisplayName}
                disabled
              />
            }
            onClose={() => setIsFormOpen(false)}
            onSaveAndClose={noop}
          />
        ) : undefined
      }
      timestamp={entry.timestamp}
    />
  );
};
